import { useEffect, useState } from "react";
import { ConfirmDialog } from "./ConfirmDialog.tsx";

export interface SessionContentResetSelection {
  clearTranscript: boolean;
  clearQuestions: boolean;
  clearTimeline: boolean;
  clearConsolidation: boolean;
}

interface ResetStatus extends SessionContentResetSelection {
  _id: string;
  status: "queued" | "running" | "complete" | "failed";
  deletedCount: number;
  errorSummary?: string | null;
}

const EMPTY_SELECTION: SessionContentResetSelection = {
  clearTranscript: false,
  clearQuestions: false,
  clearTimeline: false,
  clearConsolidation: false,
};

export function SessionContentResetDialog({
  sessionTitle,
  captureRunning,
  status,
  onStart,
  onClose,
}: {
  sessionTitle: string;
  captureRunning: boolean;
  status: ResetStatus | null | undefined;
  onStart: (selection: SessionContentResetSelection) => Promise<string>;
  onClose: () => void;
}) {
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentJob = jobId && status?._id === jobId ? status : null;
  const running = submitting || currentJob?.status === "queued" || currentJob?.status === "running";
  const complete = currentJob?.status === "complete";
  const selectedCount = Object.values(selection).filter(Boolean).length;

  useEffect(() => {
    if (currentJob?.status === "failed") {
      setError(currentJob.errorSummary ?? "Could not clear session content.");
      setJobId(null);
    }
  }, [currentJob?.errorSummary, currentJob?.status]);

  async function start() {
    if (captureRunning || selectedCount === 0 || running) return;
    setSubmitting(true);
    setError(null);
    try {
      setJobId(await onStart(selection));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not clear session content.");
    } finally {
      setSubmitting(false);
    }
  }

  function update(key: keyof SessionContentResetSelection, checked: boolean) {
    setSelection((current) => normalizeSelection({ ...current, [key]: checked }));
    setError(null);
  }

  return (
    <ConfirmDialog
      title="Clear session content?"
      confirmLabel={complete ? "Done" : "Clear selected"}
      busyLabel={currentJob ? `Clearing ${currentJob.deletedCount.toLocaleString()} items…` : "Starting…"}
      busy={running}
      destructive={!complete}
      error={error}
      confirmDisabled={!complete && (captureRunning || selectedCount === 0)}
      hideCancel={complete}
      onCancel={onClose}
      onConfirm={complete ? onClose : () => void start()}
    >
      {complete ? (
        <div className="reset-complete" role="status">
          Cleared {currentJob.deletedCount.toLocaleString()} stored items from <strong>{sessionTitle}</strong>.
          Session setup was preserved.
        </div>
      ) : (
        <>
          <p>
            Choose what to remove from <strong>{sessionTitle}</strong>. Title, join code, synopsis, themes,
            panelists, notes, categories, and settings will be kept.
          </p>
          {captureRunning && (
            <div className="reset-capture-warning" role="alert">
              Stop capture before clearing session content.
            </div>
          )}
          <label className="reset-select-all">
            <input
              type="checkbox"
              checked={selectedCount === 4}
              onChange={(event) => setSelection(event.target.checked ? {
                clearTranscript: true,
                clearQuestions: true,
                clearTimeline: true,
                clearConsolidation: true,
              } : EMPTY_SELECTION)}
            />
            <span><strong>Select all</strong></span>
          </label>
          <div className="reset-option-list">
            <ResetOption
              checked={selection.clearTranscript}
              label="Transcript"
              detail="Also clears all dependent questions and summaries."
              onChange={(checked) => update("clearTranscript", checked)}
            />
            <ResetOption
              checked={selection.clearQuestions}
              disabled={selection.clearTranscript}
              label="Questions and replies"
              detail="Includes AI suggestions, likes, starters, and generated category candidates."
              onChange={(checked) => update("clearQuestions", checked)}
            />
            <ResetOption
              checked={selection.clearTimeline}
              disabled={selection.clearTranscript || selection.clearQuestions}
              label="Timeline summaries"
              detail="Also clears the consolidated summary."
              onChange={(checked) => update("clearTimeline", checked)}
            />
            <ResetOption
              checked={selection.clearConsolidation}
              disabled={selection.clearTranscript || selection.clearQuestions || selection.clearTimeline}
              label="Consolidated summary"
              detail="Clears published and pending consolidation output."
              onChange={(checked) => update("clearConsolidation", checked)}
            />
          </div>
        </>
      )}
    </ConfirmDialog>
  );
}

function ResetOption({
  checked,
  disabled = false,
  label,
  detail,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  detail: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`reset-option ${disabled ? "dependent" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
    </label>
  );
}

function normalizeSelection(selection: SessionContentResetSelection): SessionContentResetSelection {
  if (selection.clearTranscript) {
    return {
      clearTranscript: true,
      clearQuestions: true,
      clearTimeline: true,
      clearConsolidation: true,
    };
  }
  if (selection.clearQuestions) {
    return { ...selection, clearTimeline: true, clearConsolidation: true };
  }
  if (selection.clearTimeline) return { ...selection, clearConsolidation: true };
  return selection;
}
